import React from 'react';

type Locale = 'en' | 'fi' | 'sv';

/**
 * The tab bar shared by a role's two pages, the jobs page (`/fi/tyopaikat/ammatti/{slug}`) and
 * the salary page (`/fi/palkka/{slug}`), so the two read as one role page with two views. Each
 * tab is a plain link to its own URL: the pages answer different searches ("sairaanhoitaja
 * työpaikat" vs "sairaanhoitaja palkka", zero shared URLs in Google's top 10 on 2026-10-08), so
 * they stay separate indexable pages and the tabs only join them visually. Each tab carries its
 * headline number, so the other view's answer is visible before clicking.
 *
 * In-code strings, not react-intl: the jobs page renders this inside the server-only SRP header,
 * which has no IntlProvider (see RoleHeader.tsx).
 */
const LABELS: Record<Locale, { jobs: string; salary: string; nav: string }> = {
  fi: { jobs: 'Työpaikat', salary: 'Palkka', nav: 'Ammatin näkymät' },
  sv: { jobs: 'Lediga jobb', salary: 'Lön', nav: 'Yrkets vyer' },
  en: { jobs: 'Jobs', salary: 'Salary', nav: 'Role views' },
};

export interface RoleViewTabsProps {
  locale: Locale;
  active: 'jobs' | 'salary';
  jobsHref: string | null;
  jobsCount: number | null;
  salaryHref: string | null;
  /** Already formatted for the locale ("2 864 €" / "€2,864"). */
  salaryFigure: string | null;
  className?: string;
}

function formatCount(n: number, locale: Locale): string {
  return locale === 'en' ? n.toLocaleString('en-US') : n.toLocaleString('fi-FI').replace(/[  ]/g, ' ');
}

export const RoleViewTabs: React.FC<RoleViewTabsProps> = ({ locale, active, jobsHref, jobsCount, salaryHref, salaryFigure, className = '' }) => {
  const t = LABELS[locale];
  // A tab only appears when its page exists; with one view there is nothing to switch between.
  const tabs = [
    jobsHref ? { key: 'jobs' as const, href: jobsHref, label: t.jobs, figure: jobsCount != null && jobsCount > 0 ? formatCount(jobsCount, locale) : null } : null,
    salaryHref ? { key: 'salary' as const, href: salaryHref, label: t.salary, figure: salaryFigure } : null,
  ].filter((x): x is NonNullable<typeof x> => x !== null);
  if (tabs.length < 2) return null;

  return (
    <nav aria-label={t.nav} className={`flex gap-1 border-b border-jc-border ${className}`}>
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <a
            key={tab.key}
            href={tab.href}
            aria-current={isActive ? 'page' : undefined}
            className={`-mb-px inline-flex min-h-[44px] items-baseline gap-2 border-b-[3px] px-4 pt-3 pb-2.5 text-sm transition-colors ${
              isActive
                ? 'border-jc-forest font-semibold text-jc-ink'
                : 'border-transparent font-semibold text-jc-ink-secondary hover:border-jc-border hover:text-jc-ink'
            }`}
          >
            <span>{tab.label}</span>
            {tab.figure ? (
              <span className={`font-display text-base font-bold tabular-nums text-jc-ink`}>
                {tab.figure}
              </span>
            ) : null}
          </a>
        );
      })}
    </nav>
  );
};
